/**
 * Permanent removal of a Connections row, from Settings.
 *
 * Registered Environment removal resolves each durable registration in
 * sequence, then destroys the explicit Environment once with its exact owner
 * runtime identity. A projectless Environment skips the registration phase.
 *
 * Projects are handed to the sidebar resolver as synthesized pre-connect
 * snapshots. The Environment remains a separate authoritative value, never
 * inferred from those project registrations.
 *
 * The CMMD project behind the row is KEPT and merely unlinked (FOR-32612:
 * "Forge deletes Forge projects. CMMD owns the lifecycle of CMMD projects").
 * There is deliberately no cascade option here — the saga no longer has one.
 */
import { useCallback, useState } from "react";

import type {
  CloudEnvironmentCatalogRow,
  CloudProjectCatalogRow,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import {
  refreshCmmdForgeRuntimeList,
  resolveSingletonConfig,
} from "../../cmmd/cmmdForgeRuntimeListResource";
import { CmmdForgeRuntimeApiClient } from "../../environments/primary/cmmdForgeRuntimeApi";
import { sanitizePublicUiError } from "~/productSafeLabels";
import { synthesizePreConnectCloudSnapshot } from "../sidebar/cloudProjectsGroup.logic";
import type { RemovalStepLabel } from "../sidebar/removalStepLabels";
import { removeSidebarProject } from "../sidebar/sidebarProjectRemoval";
import { toastManager } from "../ui/toast";

export interface CloudConnectionRemoval {
  readonly target: CloudRemovalTarget | null;
  readonly isRemoving: boolean;
  readonly stepLabel: RemovalStepLabel | null;
  readonly requestRemoval: (
    environment: CloudEnvironmentCatalogRow,
    rows: readonly CloudProjectCatalogRow[],
  ) => void;
  readonly requestProjectlessRemoval: (environment: CloudEnvironmentCatalogRow) => void;
  readonly removeProject: (row: CloudProjectCatalogRow) => void;
  readonly setDialogOpen: (open: boolean) => void;
  readonly confirmRemoval: () => void;
}

export type CloudRemovalTarget =
  | {
      readonly kind: "registered";
      readonly environment: CloudEnvironmentCatalogRow;
      readonly rows: readonly CloudProjectCatalogRow[];
    }
  | { readonly kind: "projectless"; readonly environment: CloudEnvironmentCatalogRow };

export function useCloudConnectionRemoval(): CloudConnectionRemoval {
  const [target, setTarget] = useState<CloudRemovalTarget | null>(null);
  const [isRemoving, setIsRemoving] = useState(false);
  const [stepLabel, setStepLabel] = useState<RemovalStepLabel | null>(null);

  const requestRemoval = useCallback(
    (environment: CloudEnvironmentCatalogRow, rows: readonly CloudProjectCatalogRow[]) => {
      if (rows.length === 0) return;
      setStepLabel(null);
      setTarget({ kind: "registered", environment, rows });
    },
    [],
  );

  const requestProjectlessRemoval = useCallback((environment: CloudEnvironmentCatalogRow) => {
    setStepLabel(null);
    setTarget({ kind: "projectless", environment });
  }, []);

  const setDialogOpen = useCallback(
    (open: boolean) => {
      // The dialog is the only surface reporting an in-flight removal; closing
      // it mid-saga would leave a destructive operation running off screen.
      if (!open && isRemoving) return;
      if (!open) {
        setTarget(null);
        setStepLabel(null);
      }
    },
    [isRemoving],
  );

  const confirmRemoval = useCallback(() => {
    if (target === null || isRemoving) return;
    setIsRemoving(true);
    setStepLabel(null);
    void (async () => {
      if (target.kind === "projectless") {
        await destroyEnvironment(target.environment);
      } else {
        for (const [index, row] of target.rows.entries()) {
          await removeSidebarProject(synthesizePreConnectCloudSnapshot(row, row.resolvedRef), {
            onProgress: setStepLabel,
          });
          setTarget({
            kind: "registered",
            environment: target.environment,
            rows: target.rows.slice(index + 1),
          });
        }
        await destroyEnvironment(target.environment);
      }
    })()
      .then(() => {
        toastManager.add({
          type: "success",
          title: "Connection removed",
          description:
            target.kind === "registered"
              ? "The environment and its projects were removed from Forge Cloud. CMMD projects were kept."
              : "The environment was removed from Forge Cloud.",
        });
        setTarget(null);
      })
      .catch((error: unknown) => {
        toastManager.add({
          type: "error",
          title: "Could not remove connection",
          description: sanitizePublicUiError(error, "The connection was not removed."),
        });
      })
      .finally(() => {
        setIsRemoving(false);
        setStepLabel(null);
      });
  }, [isRemoving, target]);

  const removeProject = useCallback((row: CloudProjectCatalogRow) => {
    setStepLabel(null);
    setIsRemoving(true);
    const project = synthesizePreConnectCloudSnapshot(row, row.resolvedRef);
    // Settings → Connections never previews or requests Checkout deletion:
    // Checkout Files are preserved by default here, and the sidebar remains
    // the only surface that can offer deleting them
    // (docs/adr/project-removal-preserves-checkout-files-by-default.md).
    void removeSidebarProject(project, { destroyRuntime: false, onProgress: setStepLabel })
      .then(() => {
        toastManager.add({
          type: "success",
          title: "Project removed",
          description: `${row.projectName} was removed from Forge Cloud. Its checkout files were kept.`,
        });
      })
      .catch((error: unknown) => {
        toastManager.add({
          type: "error",
          title: "Could not remove project",
          description: sanitizePublicUiError(error, "The project was not removed."),
        });
      })
      .finally(() => {
        setIsRemoving(false);
        setStepLabel(null);
      });
  }, []);

  return {
    target,
    isRemoving,
    stepLabel,
    requestRemoval,
    requestProjectlessRemoval,
    removeProject,
    setDialogOpen,
    confirmRemoval,
  };
}

async function destroyEnvironment(environment: CloudEnvironmentCatalogRow): Promise<void> {
  const client = new CmmdForgeRuntimeApiClient(resolveSingletonConfig());
  await client.destroyRuntime({
    runtimeId: environment.runtimeId,
    organizationId: environment.cmmdOrganizationId,
    purgeVolume: false,
  });
  refreshCmmdForgeRuntimeList();
}
